import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TuneIcon from '@mui/icons-material/Tune';

const ColumnMapper = ({
  headers = [],
  rows = [],
  mapping = {},
  onMappingChange,
  onExecuteLookup,
  loading = false,
}) => {
  const { t } = useTranslation();

  const handleModeToggle = (event, newMode) => {
    if (newMode) {
      onMappingChange({ ...mapping, mode: newMode });
    }
  };

  const handleFieldChange = (field, value) => {
    onMappingChange({ ...mapping, [field]: value });
  };

  const previewRows = rows.slice(0, 3);

  const isFormValid = mapping.mode === 'fullName' 
    ? Boolean(mapping.fullName) 
    : Boolean(mapping.lastName || mapping.firstName);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Header & Mode Selector */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <TuneIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('Column Mapping & Configuration')}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t('Select which columns contain patient names to match against clinical records.')}
              </Typography>
            </Box>

            <ToggleButtonGroup
              value={mapping.mode || 'fullName'}
              exclusive
              onChange={handleModeToggle}
              size="small"
              sx={{ bgcolor: 'action.hover' }}
            >
              <ToggleButton value="fullName" sx={{ textTransform: 'none', px: 2, fontWeight: 600 }}>
                {t('Single Full Name Column')}
              </ToggleButton>
              <ToggleButton value="separateNames" sx={{ textTransform: 'none', px: 2, fontWeight: 600 }}>
                {t('Separate Last & First Names')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Divider />

          {/* Mapping Controls Grid */}
          <Grid container spacing={2.5}>
            {mapping.mode === 'fullName' ? (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel id="full-name-select-label">{t('Patient Full Name Column')}</InputLabel>
                  <Select
                    labelId="full-name-select-label"
                    value={mapping.fullName || ''}
                    label={t('Patient Full Name Column')}
                    onChange={(e) => handleFieldChange('fullName', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>{t('Select Column')}</em>
                    </MenuItem>
                    {headers.map((header) => (
                      <MenuItem key={header} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel id="last-name-select-label">{t('Last Name (Επώνυμο)')}</InputLabel>
                    <Select
                      labelId="last-name-select-label"
                      value={mapping.lastName || ''}
                      label={t('Last Name (Επώνυμο)')}
                      onChange={(e) => handleFieldChange('lastName', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>{t('Select Column')}</em>
                      </MenuItem>
                      {headers.map((header) => (
                        <MenuItem key={header} value={header}>
                          {header}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="first-name-select-label">{t('First Name (Όνομα)')}</InputLabel>
                    <Select
                      labelId="first-name-select-label"
                      value={mapping.firstName || ''}
                      label={t('First Name (Όνομα)')}
                      onChange={(e) => handleFieldChange('firstName', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>{t('Select Column')}</em>
                      </MenuItem>
                      {headers.map((header) => (
                        <MenuItem key={header} value={header}>
                          {header}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Optional AMKA */}
            <Grid item xs={12} sm={6} md={mapping.mode === 'fullName' ? 3 : 2}>
              <FormControl fullWidth size="small">
                <InputLabel id="amka-select-label">{t('AMKA (Optional)')}</InputLabel>
                <Select
                  labelId="amka-select-label"
                  value={mapping.amka || ''}
                  label={t('AMKA (Optional)')}
                  onChange={(e) => handleFieldChange('amka', e.target.value)}
                >
                  <MenuItem value="">
                    <em>{t('None / Not in file')}</em>
                  </MenuItem>
                  {headers.map((header) => (
                    <MenuItem key={header} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Optional Phone */}
            <Grid item xs={12} sm={6} md={mapping.mode === 'fullName' ? 3 : 2}>
              <FormControl fullWidth size="small">
                <InputLabel id="phone-select-label">{t('Phone (Optional)')}</InputLabel>
                <Select
                  labelId="phone-select-label"
                  value={mapping.phone || ''}
                  label={t('Phone (Optional)')}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                >
                  <MenuItem value="">
                    <em>{t('None / Not in file')}</em>
                  </MenuItem>
                  {headers.map((header) => (
                    <MenuItem key={header} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Optional DOB */}
            {mapping.mode === 'separateNames' && (
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="dob-select-label">{t('DOB (Optional)')}</InputLabel>
                  <Select
                    labelId="dob-select-label"
                    value={mapping.dob || ''}
                    label={t('DOB (Optional)')}
                    onChange={(e) => handleFieldChange('dob', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>{t('None')}</em>
                    </MenuItem>
                    {headers.map((header) => (
                      <MenuItem key={header} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          {/* Quick Preview Table */}
          {rows.length > 0 && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                  {t('Previewing first {{count}} rows of {{total}} loaded records', {
                    count: previewRows.length,
                    total: rows.length,
                  })}
                </Typography>
                <Chip
                  label={`${rows.length} ${t('Records')}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700, height: 22 }}
                />
              </Stack>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 180 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>#</TableCell>
                      {mapping.mode === 'fullName' ? (
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark' }}>
                          {mapping.fullName || t('Full Name')} 🏷️
                        </TableCell>
                      ) : (
                        <>
                          <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark' }}>
                            {mapping.lastName || t('Last Name')} 🏷️
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark' }}>
                            {mapping.firstName || t('First Name')} 🏷️
                          </TableCell>
                        </>
                      )}
                      {mapping.amka && (
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>{mapping.amka}</TableCell>
                      )}
                      {mapping.phone && (
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>{mapping.phone}</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewRows.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ color: 'text.secondary' }}>{idx + 1}</TableCell>
                        {mapping.mode === 'fullName' ? (
                          <TableCell sx={{ fontWeight: 600 }}>{row[mapping.fullName] || '—'}</TableCell>
                        ) : (
                          <>
                            <TableCell sx={{ fontWeight: 600 }}>{row[mapping.lastName] || '—'}</TableCell>
                            <TableCell>{row[mapping.firstName] || '—'}</TableCell>
                          </>
                        )}
                        {mapping.amka && <TableCell>{row[mapping.amka] || '—'}</TableCell>}
                        {mapping.phone && <TableCell>{row[mapping.phone] || '—'}</TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Action Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              disabled={!isFormValid || loading}
              onClick={onExecuteLookup}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              sx={{
                px: 4,
                py: 1.2,
                fontWeight: 700,
                borderRadius: 2.5,
                boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.35)',
                textTransform: 'none',
                fontSize: '1rem',
              }}
            >
              {loading
                ? t('Searching & Matching Diagnoses...')
                : t('Find Diagnoses for {{count}} Patients', { count: rows.length })}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ColumnMapper;
